<template>
    <b-modal
        size="md"
        ref="modal"
        button-size="sm"
        :busy="isSubmitting"
        :no-close-on-esc="true"
        :hide-header-close="true"
        :no-close-on-backdrop="true"
        :ok-disabled="disabledOkButton"
        :title="$t('people.uploadFeedbacks')"
        @ok="($event) => onSubmit($event)"
        @cancel="routerBack()"
    >
        <h6>{{ $t('people.instructions.createNewFeedbacks') }}</h6>
        <p>
            1. {{ $t('people.instructions.downloadTemplate') }}
            <b-link @click="onClickDownloadFeedbacksTemplate" href="#">{{
                $t('people.instructions.clickingHere')
            }}</b-link>
        </p>
        <p>
            2.
            {{
                $t('people.instructions.fillInTheRequiredColumns', {
                    item: 'Email do avaliador e Email do avaliado',
                })
            }}
        </p>
        <p>3. {{ $t('people.instructions.followTheExample') }}</p>
        <h6 class="mt-1">{{ $t('people.instructions.observations') }}</h6>
        <ul>
            <li>{{ $t('people.instructions.doNotRenameColumns') }}</li>
            <li>{{ $t('people.instructions.noFormulas') }}</li>
            <li>{{ $t('people.instructions.acceptedFormats') }}</li>
            <li>{{ $t('people.instructions.feedbackDateFormat') }}</li>
        </ul>
        <b-form-group :label="$t('form.label.file')" label-class="font-weight-bold">
            <b-form-file
                id="file-pdi"
                :state="fileState"
                v-model="$v.form.file.$model"
                :accept="acceptedExtensions"
                :placeholder="$t('form.placeholder.chooseFile')"
                :browse-text="$t('form.button.search')"
            />
            <b-form-invalid-feedback :state="fileState">
                <span v-if="!$v.form.file.required">
                    {{ $t('form.validations.required') }}
                </span>
                <span v-if="!$v.form.file.fileTooIsLarge">{{ $t('form.validations.fileTooLarge') }}</span>
                <span v-if="!$v.form.file.invalidFileExtension">{{ $t('form.validations.invalidExtension') }}</span>
            </b-form-invalid-feedback>
        </b-form-group>
        <template slot="modal-ok">
            <span v-if="isSubmitting">
                <i class="fa fa-spinner fa-spin px-2"></i>
            </span>
            <span v-else>
                <i class="fas fa-upload"></i>
                {{ $t('people.upload') }}
            </span>
        </template>
    </b-modal>
</template>

<script>
import i18n from '@/i18n'
import Swal from 'sweetalert2'
import { onlyAdministratorFunctionAlert } from '../../alerts'
import { validationMixin } from 'vuelidate'
import { uploadToApi, downloadFeedbacksTemplate } from './api'
import notification, { success } from '@/lib/notification'

notification.init()

const allowedExtensions = ['xls', 'xlsx', 'xml', 'xlsm', 'csv']
const excelColumns = {
    valuer: 'Email do Avaliador',
    evaluated: 'Email do Avaliado',
}

export default {
    name: 'UploadFeedbacksModal',
    props: {
        canUploadUsers: {
            type: Boolean,
            default: false,
        },
    },
    mixins: [validationMixin],
    mounted() {
        if (this.canUploadUsers) {
            this.$refs.modal.show()
        } else {
            this.handlerUserWithoutAuthorization()
        }
    },
    validations: {
        form: {
            file: {
                required: (file) => {
                    return file !== null
                },
                invalidFileExtension: (file) => {
                    if (file) {
                        const fileExtension = file.name.split('.').pop()
                        return allowedExtensions.includes(fileExtension)
                    }
                    return false
                },
                fileIsTooLarge: (file) => {
                    if (file) {
                        return !(file.size >> 20 > 20)
                    }
                    return false
                },
            },
        },
    },
    data() {
        return {
            fileToUpload: null,
            form: {
                file: null,
            },
            isSubmitting: false,
            disabledOkButton: false,
        }
    },
    methods: {
        routerBack() {
            this.$router.push({ path: '/v2/people/list' })
        },
        onSubmit(event) {
            event.preventDefault()
            if (!this.validate()) {
                return
            }

            this.isSubmitting = true
            uploadToApi(this.fileToUpload)
                .then((response) => {
                    const error = response.data.messages[0]
                    if (error.status == 'success') {
                        success(i18n.t('pdis.notification.feedbacksCreatedSuccessfully'))
                        this.$nextTick(() => {
                            this.$emit('updated')
                            this.routerBack()
                        })
                    } else {
                        switch (error.params.type) {
                            case 'invalidData': {
                                let requiredColumns = this.getExcelRequiredColumns(error.params.attributes)
                                Swal({
                                    type: 'error',
                                    title: i18n.t('notification.somethingWentWrong'),
                                    text: i18n.t('notification.excel.validation.requiredColumnsNotFound', {
                                        item: requiredColumns,
                                    }),
                                })
                                break
                            }
                            default:
                                break
                        }
                    }
                })
                .finally(() => {
                    this.isSubmitting = false
                })
        },
        handlerUserWithoutAuthorization() {
            this.$refs.modal.hide()
            return onlyAdministratorFunctionAlert().then(() => this.routerBack())
        },
        onClickDownloadFeedbacksTemplate() {
            downloadFeedbacksTemplate().then((response) => {
                this.forceFileDownload(response)
            })
        },
        forceFileDownload(response) {
            let a = document.createElement('a')
            a.download = 'upload_feedbacks_coblue.xlsx'
            a.target = '_blank'
            a.style = 'display: none'
            document.body.appendChild(a)
            a.href = URL.createObjectURL(new Blob([response.data]))
            a.click()
        },
        getExcelRequiredColumns(params) {
            const columns = Object.values(params)
            return this.getExcelColumns(columns)
        },
        getExcelColumns(columns) {
            return columns.map((column) => {
                return excelColumns[column]
            })
        },
        validate() {
            this.$v.$touch()
            return !this.$v.$invalid
        },
    },
    watch: {
        seletedFile() {
            const file = this.seletedFile
            if (file) {
                let reader = new FileReader()
                reader.readAsDataURL(file)
                reader.onload = (event) => {
                    this.fileToUpload = {
                        blob: event.target.result,
                        filename: file.name,
                    }
                }
            }
        },
    },
    computed: {
        seletedFile() {
            return this.$v.form.file.$model
        },
        fileState() {
            return this.$v.form.file.$dirty ? !this.$v.form.file.$invalid : null
        },
        acceptedExtensions() {
            return allowedExtensions.map((ext) => '.' + ext).join(', ')
        },
    },
}
</script>
